/* eslint-disable no-nested-ternary */
/* eslint-disable complexity */
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { FaBars } from 'react-icons/fa';
import { FaXmark } from 'react-icons/fa6';

import {
  propTypesActiveDocMeta,
  propTypesLocalization,
} from '../../../constants/Proptypes';
import { propTypesNavigation } from './Header.proptypes';

import Button, { ButtonAppearance } from '../../../../ui/Atoms/Button';

import { BlogNavigationDesktop } from './BlogNavigationDesktop';
import { SearchBar } from './SearchBar';
import { WebsiteNavigationDesktop } from './WebsiteNavigationDesktop';

import { addTrackingParamsToUrl } from '../../../utils/trackingParams';

import { DocumentType } from '../../../constants/Enums';

import { BlogNavigationMobile } from './BlogNavigationMobile';
import * as S from './Header.styles';
import { WebsiteNavigationMobile } from './WebsiteNavigationMobile';

const RootLogo = ({ rootLink }) => {
  return (
    <S.LogoLink href={rootLink}>
      <S.Logo
        alt={'Wooclap blue logo'}
        src={'https://www.wooclap.com/images/wooclap-logo.svg'}
      />
    </S.LogoLink>
  );
};

export const Header = ({
  navigation,
  activeDocMeta,
  localization,
  isNavbarHidden,
}) => {
  const modalRef = useRef(null);

  const [isSearchBarDisplayed, setIsSearchBarDisplayed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: {
      searchbar_placeholder: placeholder,
      event_searchbar_placeholder: eventPlaceholder,
      share_event_label: shareEventLabel,
      share_event_link: { url: shareEventLink, target: shareEventTarget } = {},
    },
  } = localization ?? {};

  if (!navigation || !activeDocMeta) {
    return null;
  }

  const { data } = navigation || {};
  const { lang, type } = activeDocMeta || {};

  const isWebsite =
    type === DocumentType.PAGE ||
    type === DocumentType.CONTACT ||
    type === DocumentType.NOTFOUND ||
    type === DocumentType.HOMEPAGEWEB ||
    type === DocumentType.PRICING ||
    type === DocumentType.CONTACT ||
    type === DocumentType.GLOSSARY ||
    type === DocumentType.DEFINITION;

  const isEvent =
    type === DocumentType.EVENTGALLERY ||
    type === DocumentType.EVENTTAG ||
    type === DocumentType.EVENT;

  const documentType = isWebsite ? 'website' : isEvent ? 'event' : 'blog';

  const rootLink = `https://www.wooclap.com/${
    lang === 'en-gb' ? '' : `${lang.slice(0, 2)}/`
  }`;

  const hasBlogNavigation = documentType === 'blog' && !isSearchBarDisplayed;
  const hasSearchBar = documentType === 'blog' || documentType === 'event';

  const {
    additional_label: additionalLabel,
    additional_link: { url: additionalUrl, target: additionalTarget },
    additional_datatrack: additionalDataTrack,
    signin_label: signinLabel,
    signin_url: { url: signinLink, target: signinTarget } = {},
    signin_datatrack: signinDatatrack,
    signup_label: signupLabel,
    signup_url: { url: signupLink, target: signupTarget } = {},
    signup_datatrack: signupDatatrack,
    header_aria_label: headerAriaLabel,
  } = data ?? {};

  const [urls, setUrls] = useState({
    signinLink,
    signupLink,
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setUrls({
        signinLink: addTrackingParamsToUrl(signinLink),
        signupLink: addTrackingParamsToUrl(signupLink),
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const handleCookieChange = () => {
      setUrls({
        signinLink: addTrackingParamsToUrl(signinLink),
        signupLink: addTrackingParamsToUrl(signupLink),
      });
    };

    document.addEventListener('cookiechange', handleCookieChange);

    return () => {
      document.removeEventListener('cookiechange', handleCookieChange);
    };
  }, []);

  /**
   * Prevent body scroll when modal is open
   */
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  /**
   * Close modal on Escape key press and handle focus trap
   */
  useEffect(() => {
    if (!isModalOpen) return;

    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsModalOpen(false);
      }

      if (e.key === 'Tab' && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll(
          'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;

        const firstEl = focusables[0];
        const lastEl = focusables[focusables.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, setIsModalOpen]);

  const websiteDropdowns = Array.from({ length: 5 }, (_, i) => {
    const index = i + 1;

    return {
      title: data[`menu_${index}_title`],
      moreLabel: data[`menu_${index}_more_label`],
      moreLink: data[`menu_${index}_more_link`] || {},
      sections: data[`menu_${index}`],
    };
  });

  const blogDropdowns = Array.from({ length: 4 }, (_, i) => {
    const index = i + 1;

    return {
      column: index,
      label: data[`dropdown_${index}_label`],
      content: data[`dropdown_${index}`] || [],
      optionalLabel: data[`dropdown_${index}_optional_link`],
      optionalUrl: data[`dropdown_${index}_optional_url`],
    };
  });

  return (
    <S.MainContainer>
      <S.MainWrapper>
        <S.NavigationWrapper>
          <RootLogo rootLink={rootLink} />
          <S.MenuLinksWrapper>
            <S.MenuLinks>
              {/* Blog  Menu Header */}
              {!isNavbarHidden && hasBlogNavigation && (
                <BlogNavigationDesktop
                  blogDropdowns={blogDropdowns}
                  headerAriaLabel={headerAriaLabel}
                />
              )}

              {/* Website Menu Header */}
              {!isNavbarHidden && documentType === 'website' && (
                <WebsiteNavigationDesktop
                  websiteDropdowns={websiteDropdowns}
                  headerAriaLabel={headerAriaLabel}
                />
              )}
            </S.MenuLinks>

            {/* Optional Website Link */}
            <S.LeadLinks>
              {documentType === 'website' &&
                additionalLabel &&
                additionalUrl && (
                  <S.AdditionalLinkWrapper>
                    <Button
                      appearance={ButtonAppearance.MINIMAL}
                      target={additionalTarget}
                      dataTrackAttribute={additionalDataTrack}
                      link={additionalUrl}
                    >
                      {additionalLabel}
                    </Button>
                    <S.VerticalLine />
                  </S.AdditionalLinkWrapper>
                )}

              {/* Search Bar */}
              {hasSearchBar && (
                <S.SearchBarWrapper>
                  <SearchBar
                    isSearchBarDisplayed={isSearchBarDisplayed}
                    setIsSearchBarDisplayed={setIsSearchBarDisplayed}
                    placeholder={
                      documentType === 'event' ? eventPlaceholder : placeholder
                    }
                    lang={lang}
                    isEvent={documentType === 'event' ? true : false}
                  />
                </S.SearchBarWrapper>
              )}

              {/* Templates Link */}
              {documentType === 'event' ? (
                <S.ButtonWrapper isEvent={true}>
                  <Button
                    altText={shareEventLabel}
                    link={shareEventLink}
                    target={shareEventTarget}
                  >
                    {shareEventLabel}
                  </Button>
                </S.ButtonWrapper>
              ) : (
                // Sign In / Sign Up Links
                <S.ButtonWrapper hasMargin={documentType === 'blog'}>
                  <S.StyledSigninButton
                    altText={signinLabel}
                    appearance={ButtonAppearance.MINIMAL}
                    dataTrackAttribute={signinDatatrack}
                    link={urls?.signinLink}
                    target={signinTarget}
                  >
                    {signinLabel}
                  </S.StyledSigninButton>
                  <Button
                    altText={signupLabel}
                    dataTrackAttribute={signupDatatrack}
                    link={urls?.signupLink}
                    target={signupTarget}
                  >
                    {signupLabel}
                  </Button>
                </S.ButtonWrapper>
              )}

              <S.BurgerButton
                aria-label='Open menu'
                onClick={() => {
                  setIsModalOpen(true);
                }}
              >
                <FaBars />
              </S.BurgerButton>
            </S.LeadLinks>
          </S.MenuLinksWrapper>
        </S.NavigationWrapper>
      </S.MainWrapper>
      <S.HorizontalLine />

      {/* Mobile Nav */}
      <S.ModalOverlay
        isOpen={isModalOpen}
        aria-modal='true'
        role='dialog'
        ref={modalRef}
      >
        <S.ModalHeader>
          <RootLogo rootLink={rootLink} />
          <S.CloseButton
            aria-label='Close menu'
            onClick={() => setIsModalOpen(false)}
          >
            <FaXmark />
          </S.CloseButton>
        </S.ModalHeader>
        <S.HorizontalLine />

        <S.Nav>
          {!isNavbarHidden && documentType === 'website' && (
            <WebsiteNavigationMobile websiteDropdowns={websiteDropdowns} />
          )}

          {!isNavbarHidden && hasBlogNavigation && (
            <BlogNavigationMobile blogDropdowns={blogDropdowns} />
          )}
        </S.Nav>

        <S.MobileActionButtons>
          <Button
            altText={signinLabel}
            appearance={ButtonAppearance.MINIMAL}
            dataTrackAttribute={signinDatatrack}
            link={urls?.signinLink}
            target={signinTarget}
          >
            {signinLabel}
          </Button>
          <Button
            altText={signupLabel}
            dataTrackAttribute={signupDatatrack}
            link={urls?.signupLink}
            target={signupTarget}
          >
            {signupLabel}
          </Button>
        </S.MobileActionButtons>
      </S.ModalOverlay>
    </S.MainContainer>
  );
};

Header.propTypes = {
  /**
   * Language data from the current Prismic document.
   */
  activeDocMeta: PropTypes.shape(propTypesActiveDocMeta),
  /**
   * Hide the navbar links, the breadcrumb and the promo banner if true.
   */
  isNavbarHidden: PropTypes.bool,
  /**
   * Prismic Localization object.
   */
  localization: PropTypes.shape(propTypesLocalization),
  /**
   * Navigation document.
   */
  navigation: PropTypes.shape(propTypesNavigation),
};
